import LandingPage from 'components/pages/LandingPage/LandingPage';
import { DASHBOARD } from 'components/pages/pages';
import Page, { PageOptions } from 'components/templates/Page/Page';
import LANDING_PAGES from 'constants/landingPages';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetStaticProps, GetStaticPropsContext } from 'next/types';
import { ParsedUrlQuery } from 'querystring';
import React from 'react';
import { Environment, fetchQuery, GraphQLTaggedNode } from 'react-relay';
import { Variables } from 'relay-runtime';
import createRelayEnvironment from 'relay/createRelayEnvironment';
import type { OfferType } from 'utils/offers';
import nextI18NextConfig from '../../next-i18next.config.js';

export function buildPage<T>(Component: React.ComponentType<T>, options: PageOptions) {
  return function BuildPage(props: T & { namespace?: string }) {
    return (
      <Page namespace={props.namespace} {...options}>
        <Component {...props} />
      </Page>
    );
  };
}

export function buildLandingPage(keyword: string, isVanillaText?: boolean) {
  const landingPage = LANDING_PAGES[keyword];

  return buildPage(
    () => (
      <LandingPage
        leftColumnContent={landingPage.leftColumnContent}
        isVanillaText={isVanillaText}
      />
    ),
    {
      skipSSR: true,
      loggedInRedirect: DASHBOARD.path,
      unlisted: true,
      metaData: landingPage.metaData,
    },
  );
}

interface StaticPageOptions {
  withoutHeader?: boolean;
  withoutHeaderOnMobile?: boolean;
  withoutSubNav?: boolean;
  withoutFooter?: boolean;
  withoutFooterOnMobile?: boolean;
  fadedPageBg?: boolean;
  fadedPageBgMobile?: boolean;
  isLandingPage?: boolean;
  sessionId?: string | string[] | null;
  username?: string | string[] | null;
  offerType?: OfferType;
  brandTakeover?: boolean;
}

type QueryVarGenerator = (context: GetStaticPropsContext<ParsedUrlQuery>) => Variables;

export function buildStaticProps(
  namespace: string,
  options: StaticPageOptions = {},
): GetStaticProps {
  return async (context) => {
    const translations = await serverSideTranslations(
      context.locale || context.defaultLocale || 'en',
      ['common', 'cookies', namespace],
      nextI18NextConfig as any,
      context.locale && /^[a-z]{2}-[A-Z]{2}$/.test(context.locale)
        ? [context.locale.split('-')[0]]
        : [],
    );
    return {
      props: {
        ...(nextI18NextConfig.saveMissing ? {} : translations),
        ...options,
        namespace,
      },
    };
  };
}

export function buildRelayEnvironment(locale: string) {
  return createRelayEnvironment({
    getAccessToken: () => Promise.resolve(null),
    onError: (err) => console.warn(err),
    locale: locale === 'default' ? 'en-us' : locale,
  });
}

export function buildQueryPreloader(
  query: GraphQLTaggedNode,
  variables?: Variables | QueryVarGenerator,
): GetStaticProps {
  const getStaticProps: GetStaticProps = async function getStaticProps(context) {
    const environment = buildRelayEnvironment(context.locale || 'en-us');
    const vars = typeof variables === 'function' ? variables(context) : variables;
    await fetchQuery(environment as Environment, query, vars || {}).toPromise();
    const relayEnv = environment.getStore().getSource().toJSON();
    return {
      revalidate: 30 * 60,
      props: {
        relayEnv: relayEnv,
      },
    };
  };
  return getStaticProps;
}
